/*
 * Step1UploadPDFs - PDF upload and AI extraction step
 *
 * UI/UX DESCRIPTION:
 * - File upload dropzone with drag & drop support
 * - AI model selector for extraction
 * - File preview with remove functionality
 * - Loading state during AI processing
 * - Error handling for extraction failures
 *
 * FUNCTIONALITY:
 * - Upload up to 3 PDF/image files
 * - Select AI model for extraction
 * - Process files with AI to extract rebate rules
 * - Create new draft version with extracted rules
 * - Handle extraction errors gracefully
 */

import React, { useState } from 'react';
import {
  UploadIcon,
  CircleNotchIcon,
  WarningCircleIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { Button } from 'src/components/ui/button';
import { Card, CardContent } from 'src/components/ui/card';
import { AlertContainer, AlertDescription } from 'src/components/ui/alert';
import FileUploadDropzone from 'src/components/documents/FileUploadDropzone';
import AIModelSelector from 'src/components/common/AIModelSelector';
import { AI_MODELS, SupportedModelId } from 'src/types/aiModels';
import { UploadProgress } from 'src/types/UserDocuments';
import FileIcon from 'src/components/documents/FileIcon';
import PrimaryButton from 'src/components/common/PrimaryButton';
import {
  useExtractRulesOnly,
  ExtractRulesOnlyResponse,
} from 'src/api/rebates/useCreateVersionWithExtraction';
import { wrapExtractedRule } from './utils';
import { RuleWithVersionState } from './types';

// Types
interface RebateAgreement {
  id: string;
  original_agreement_id: string;
  name: string;
  status: string;
}

interface RebateVersion {
  id: string;
  versionNumber: number;
  status: string;
}

interface ExtractionMetadata {
  processingTime: number;
  confidence: number;
  model: string;
}

interface Step1UploadPDFsProps {
  agreement: RebateAgreement;
  latestVersion: RebateVersion;
  onComplete: (
    files: File[],
    wrappedRules: RuleWithVersionState[],
    productGroups: ExtractRulesOnlyResponse['productGroups'],
    extractionMetadata: ExtractRulesOnlyResponse['extractionMetadata'],
  ) => void;
  onError: (error: string) => void;
  isLoading: boolean;
  error?: string;
  selectedFiles: File[];
  setSelectedFiles: (files: File[]) => void;
}

const MAX_FILES = 3;

const Step1UploadPDFs: React.FC<Step1UploadPDFsProps> = ({
  agreement,
  latestVersion,
  onComplete,
  onError,
  isLoading,
  error,
  selectedFiles,
  setSelectedFiles,
}) => {
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [selectedModel, setSelectedModel] = useState<SupportedModelId>(
    AI_MODELS.CLAUDE_OPUS_4_1,
  );
  const [extractionError, setExtractionError] = useState<string>('');

  const extractRulesMutation = useExtractRulesOnly();

  const handleFilesSelected = (files: File[]) => {
    // Validate file types
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
    ];

    const invalidFiles = files.filter(
      (file) => !allowedTypes.includes(file.type),
    );
    if (invalidFiles.length > 0) {
      setExtractionError('Please select only PDF, JPG, JPEG, or PNG files.');
      return;
    }

    // Merge new files with existing files, avoiding duplicates
    const existingFileNames = new Set(selectedFiles.map((f) => f.name));
    const newFiles = files.filter((file) => !existingFileNames.has(file.name));

    const totalFiles = selectedFiles.length + newFiles.length;

    // Check if adding these files would exceed MAX_FILES
    if (totalFiles > MAX_FILES) {
      const remainingSlots = MAX_FILES - selectedFiles.length;
      if (remainingSlots <= 0) {
        setExtractionError(
          `You have already selected ${MAX_FILES} files. Please remove some files before adding more.`,
        );
        return;
      }
      setExtractionError(
        `You can only add ${remainingSlots} more file${remainingSlots !== 1 ? 's' : ''} (maximum ${MAX_FILES} files total).`,
      );
      return;
    }

    setExtractionError('');
    setSelectedFiles([...selectedFiles, ...newFiles]);
  };

  const handleRemoveFile = (index: number) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
  };

  const handleExtract = async () => {
    if (selectedFiles.length === 0) {
      setExtractionError('Please select at least one file to upload.');
      return;
    }

    setExtractionError('');

    try {
      const result = await extractRulesMutation.mutateAsync({
        versionGroupId: agreement.original_agreement_id,
        files: selectedFiles,
        model: selectedModel,
      });

      // Backend now returns RebateRule format (camelCase) directly - just wrap them
      const wrappedRules = result.data.extractedRules.map(wrapExtractedRule);

      onComplete(
        selectedFiles,
        wrappedRules, // Pass wrapped rules instead of raw extracted rules
        result.data.productGroups,
        result.data.extractionMetadata,
      );
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to extract rules from uploaded files';
      setExtractionError(errorMessage);
      onError(errorMessage);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Button is enabled only if files are selected and extraction is not in progress
  const canExtract =
    selectedFiles.length > 0 &&
    !extractRulesMutation.isPending &&
    !extractionError;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold mb-2">
          Upload Agreement Documents
        </h2>
        <p className="text-muted-foreground">
          Upload PDF or image files of your rebate agreement for AI-powered rule
          extraction. The AI will analyze the documents and extract rebate rules
          automatically.
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-end pt-2 pb-4 border-b">
        <PrimaryButton
          onClick={handleExtract}
          disabled={!canExtract}
          className="gap-2"
        >
          {extractRulesMutation.isPending ? (
            <>
              <CircleNotchIcon className="h-4 w-4 animate-spin" />
              Extracting Rules...
            </>
          ) : (
            <>
              <UploadIcon className="h-4 w-4" />
              Extract Rules
            </>
          )}
        </PrimaryButton>
      </div>

      {/* Error Display */}
      {(error || extractionError) && (
        <AlertContainer variant="destructive">
          <WarningCircleIcon className="h-4 w-4" />
          <AlertDescription>{error || extractionError}</AlertDescription>
        </AlertContainer>
      )}

      {/* AI Model Selection */}
      {/* <div className="space-y-2">
        <h3 className="text-lg font-medium">AI Model</h3>
        <AIModelSelector
          label="Select AI Model"
          selectedModel={selectedModel}
          onModelChange={setSelectedModel}
          disabled={extractRulesMutation.isPending}
          placeholder="Choose model for extraction..."
          fullWidth={true}
        />
        <p className="text-sm text-muted-foreground">
          Different models may provide varying accuracy and speed for rule
          extraction.
        </p>
      </div> */}

      {/* File Upload */}
      <div className="space-y-2">
        <h3 className="text-lg font-medium">
          Upload Files (up to {MAX_FILES} files)
        </h3>
        <FileUploadDropzone
          onFilesSelected={handleFilesSelected}
          uploadProgress={uploadProgress}
          isUploading={extractRulesMutation.isPending}
          maxFiles={Math.max(0, MAX_FILES - selectedFiles.length)}
          disabled={
            extractRulesMutation.isPending || selectedFiles.length >= MAX_FILES
          }
        />
        <p className="text-sm text-muted-foreground">
          Supported formats: PDF, JPG, JPEG, PNG
        </p>
      </div>

      {/* Selected Files Preview */}
      {selectedFiles.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-lg font-medium">
            Selected Files ({selectedFiles.length})
          </h3>
          <div className="space-y-2">
            {selectedFiles.map((file, index) => (
              <Card key={index}>
                <CardContent className="py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileIcon
                        fileName={file.name}
                        size="small"
                        showBackground={false}
                      />
                      <div>
                        <p className="text-sm font-medium">{file.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveFile(index)}
                      disabled={extractRulesMutation.isPending}
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Extraction Status */}
      {extractRulesMutation.isPending && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CircleNotchIcon className="h-5 w-5 animate-spin text-primary" />
              <div>
                <p className="font-medium">Processing Documents</p>
                <p className="text-sm text-muted-foreground">
                  AI is analyzing your agreement documents to extract rebate
                  rules...
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Step1UploadPDFs;
